import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, X, Plus, Trash2, Search, Building2, UserCheck, Calendar, DollarSign, Check, Eye, Copy, Link as LinkIcon, Upload, FileText, BarChart3, Clock, CheckCircle, AlertCircle, Users, Pencil, GripVertical, Handshake, Info, CheckSquare, Download, ExternalLink, FileImage } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Collab, createDocument, updateDocument, deleteDocument, supabase } from '../lib/supabase';
import { useNotification } from '../contexts/NotificationContext';
import InfluencerAvatar from '../components/InfluencerAvatar';
import CustomListbox from '../components/CustomListbox';

type TimelineItem = {
  id: string;
  collabId: string;
  stepName: string;
  deadline?: string | Date | { seconds: number; nanoseconds?: number } | null;
  status: 'todo' | 'in_progress' | 'done' | 'late';
  order: number;
  influencerId?: string;
  influencerName?: string;
};

const toDateInputValue = (v: TimelineItem['deadline']) => {
  if (!v) return '';
  if (typeof v === 'string') return new Date(v).toISOString().slice(0, 10);
  // Firestore Timestamp-like
  if (typeof v === 'object' && 'seconds' in v) {
    return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  }
  return new Date(v as Date).toISOString().slice(0, 10);
};

const encodeSupabaseUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const encodedParts = pathParts.map((part, index) =>
      index === pathParts.length - 1 ? encodeURIComponent(part) : part
    );
    urlObj.pathname = encodedParts.join('/');
    return urlObj.toString();
  } catch {
    return url;
  }
};

const sanitizeFileName = (fileName: string): string => {
  return fileName
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');
};

export default function CollaborationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewingInfluencerId = searchParams.get('influencerId');
  const { showNotification } = useNotification();
  const [campaign, setCampaign] = useState<Collab | null>(null);
  const [timelineItems, setTimelineItems] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [copiedLink, setCopiedLink] = useState(false);
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [isEditingTimeline, setIsEditingTimeline] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const [selectedInfluencerId, setSelectedInfluencerId] = useState<string>('');
  const [selectedInfluencerName, setSelectedInfluencerName] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const [timelineForm, setTimelineForm] = useState({
    stepName: '',
    deadline: '',
    status: 'todo',
    order: 0,
    influencerId: '',
    influencerName: ''
  });
  
  const [documentForm, setDocumentForm] = useState({
    name: '',
    type: 'brief',
    visibleForBrand: false,
    influencerId: '',
    influencerName: '',
    url: ''
  });

  const [uploadMode, setUploadMode] = useState<'file' | 'link'>('file');
  
  const [statsForm, setStatsForm] = useState({
    creator: '',
    views: 0,
    likes: 0,
    engagementRate: 0,
    postUrl: '',
    postThumb: '',
    influencerId: '',
    influencerName: ''
  });

  useEffect(() => {
    if (id) {
      loadCampaignData();
    }
  }, [id]);

  const loadCampaignData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Récupérer les détails de la collaboration depuis Supabase
      const { data: campaignData, error: campaignError } = await supabase
        .from('collabs')
        .select('*')
        .eq('id', id!)
        .maybeSingle();

      if (campaignError || !campaignData) {
        setError('Cette collaboration n\'existe pas');
        setIsLoading(false);
        return;
      }

      // Mapper les champs snake_case vers camelCase pour la compatibilité
      const mappedCampaignData = {
        ...campaignData,
        linkToken: campaignData.link_token,
        linkActive: campaignData.link_active
      };

      // Enrichir les données des influenceurs avec leurs profils complets
      if (mappedCampaignData.influencers && mappedCampaignData.influencers.length > 0) {
        // Normaliser les noms de champs des influenceurs
        mappedCampaignData.influencers = mappedCampaignData.influencers.map((inf: any) => ({
          ...inf,
          fullName: inf.fullName || inf.full_name,
          profileImageUrl: inf.profileImageUrl || inf.profile_image_url
        }));

        const influencerIds = mappedCampaignData.influencers
          .map((inf: any) => inf.id)
          .filter((id: string) => id && id.trim() !== '');

        if (influencerIds.length > 0) {
          const { data: influencerProfiles, error: influencerError } = await supabase
            .from('influencers')
            .select('id, display_name, profile_image_url, username, followers_count')
            .in('id', influencerIds);

          if (!influencerError && influencerProfiles) {
            // Enrichir chaque influenceur avec son profil complet
            mappedCampaignData.influencers = mappedCampaignData.influencers.map((inf: any) => {
              const profile = influencerProfiles.find(p => p.id === inf.id);
              if (profile) {
                return {
                  ...inf,
                  fullName: inf.fullName || profile.display_name,
                  profile_image_url: profile.profile_image_url,
                  profileImageUrl: profile.profile_image_url,
                  instagramUsername: profile.username,
                  followers: profile.followers_count
                };
              }
              return inf;
            });
          }
        }
      }

      setCampaign(mappedCampaignData as Collab);

      // Récupérer les éléments de timeline spécifiques à cette collaboration depuis Supabase
      const { data: timelineData, error: timelineError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('collab_id', id)
        .order('order_index', { ascending: true });

      if (!timelineError && timelineData) {
        let filteredTimeline = timelineData;

        if (viewingInfluencerId) {
          filteredTimeline = timelineData.filter(item =>
            !item.influencer_id ||
            item.influencer_id === '' ||
            item.influencer_id === viewingInfluencerId
          );
        }

        const mappedTimelineData = filteredTimeline.map(item => ({
          ...item,
          stepName: item.step_name,
          collabId: item.collab_id,
          influencerId: item.influencer_id,
          influencerName: item.influencer_name,
          orderIndex: item.order_index,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }));
        setTimelineItems(mappedTimelineData);
      }

      // Récupérer les documents spécifiques à cette collaboration depuis Supabase
      const { data: documentsData, error: documentsError } = await supabase
        .from('collab_documents')
        .select('*')
        .eq('collab_id', id);

      if (!documentsError && documentsData) {
        let filteredDocuments = documentsData;

        console.log('DEBUG Documents - viewingInfluencerId:', viewingInfluencerId);
        console.log('DEBUG Documents - All documents:', documentsData.map(d => ({
          name: d.name,
          influencer_id: d.influencer_id
        })));

        if (viewingInfluencerId) {
          filteredDocuments = documentsData.filter(doc =>
            !doc.influencer_id ||
            doc.influencer_id === '' ||
            doc.influencer_id === viewingInfluencerId
          );
          console.log('DEBUG Documents - Filtered documents:', filteredDocuments.map(d => ({
            name: d.name,
            influencer_id: d.influencer_id
          })));
        }

        const mappedDocumentsData = filteredDocuments.map(doc => ({
          ...doc,
          collabId: doc.collab_id,
          fileUrl: doc.file_url,
          fileName: doc.file_name,
          fileSize: doc.file_size,
          visibleForBrand: doc.visible_for_brand,
          influencerId: doc.influencer_id,
          influencerName: doc.influencer_name,
          createdAt: doc.created_at,
          updatedAt: doc.updated_at
        }));
        setDocuments(mappedDocumentsData);
      }

      // Récupérer les statistiques spécifiques à cette collaboration depuis Supabase
      const { data: statsData, error: statsError } = await supabase
        .from('collab_stats')
        .select('*')
        .eq('collab_id', id);

      if (!statsError && statsData) {
        let filteredStats = statsData;

        if (viewingInfluencerId) {
          filteredStats = statsData.filter(stat =>
            !stat.influencer_id ||
            stat.influencer_id === '' ||
            stat.influencer_id === viewingInfluencerId
          );
        }

        setStats(filteredStats);
      }
      
    } catch (error) {
      console.error('Error loading campaign data:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimelineStatusChange = async (itemId: string, newStatus: string) => {
    try {
      setIsUpdating(true);

      const updatedTimelineItems = timelineItems.map(item => {
        if (item.id === itemId) {
          return { ...item, status: newStatus };
        }
        return item;
      });

      setTimelineItems(updatedTimelineItems);

      const { error } = await supabase
        .from('timeline_items')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', itemId);

      if (error) throw error;

    } catch (error) {
      console.error('Error updating timeline status:', error);
      setError('Erreur lors de la mise à jour du statut');
      await loadCampaignData();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseTimelineModal = () => {
    setIsTimelineModalOpen(false);
    setIsEditingTimeline(false);
    setEditingItemId(null);
    resetTimelineForm();
    setSelectedInfluencerId('');
    setSelectedInfluencerName('');
  };

  const onDragStart = (id: string) => {
    setDraggingId(id);
  };

  const onDragOver = (overId: string) => {
    if (!draggingId || draggingId === overId) return;

    const items = [...timelineItems];
    const fromIndex = items.findIndex(item => item.id === draggingId);
    const toIndex = items.findIndex(item => item.id === overId);

    if (fromIndex === -1 || toIndex === -1) return;

    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);

    const withOrder = items.map((item, idx) => ({ ...item, order: idx }));
    setTimelineItems(withOrder);
  };

  const onDrop = async () => {
    if (!draggingId) return;
    setDraggingId(null);

    try {
      for (const [idx, item] of timelineItems.entries()) {
        await supabase
          .from('timeline_items')
          .update({ order_index: idx, updated_at: new Date().toISOString() })
          .eq('id', item.id);
      }
    } catch (error) {
      console.error('Error updating timeline order:', error);
      await loadCampaignData();
    }
  };

  const handleDeleteTimelineItem = async (itemId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette étape ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('timeline_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setTimelineItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting timeline item:', error);
      setError('Erreur lors de la suppression de l\'étape');
      setTimeout(() => setError(null), 3000);
    }
  };

  const openEditTimelineItem = (item: TimelineItem) => {
    setTimelineForm({
      stepName: item.stepName || '',
      deadline: toDateInputValue(item.deadline),
      status: item.status || 'todo',
      order: item.order ?? 0,
      influencerId: item.influencerId || '',
      influencerName: item.influencerName || ''
    });
    setSelectedInfluencerId(item.influencerId || '');
    setSelectedInfluencerName(item.influencerName || '');
    setEditingItemId(item.id);
    setIsEditingTimeline(true);
    setIsTimelineModalOpen(true);
  };

  const handleAddTimelineItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const base = {
        collab_id: id!,
        step_name: timelineForm.stepName,
        deadline: timelineForm.deadline ? timelineForm.deadline : null,
        status: timelineForm.status,
        influencer_id: selectedInfluencerId || null,
        influencer_name: selectedInfluencerName || null,
      };

      if (isEditingTimeline && editingItemId) {
        const { error } = await supabase
          .from('timeline_items')
          .update({
            ...base,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingItemId);

        if (error) {
          console.error('Error updating timeline item:', error);
          showNotification(`Erreur lors de la mise à jour: ${error.message}`, 'error');
          throw error;
        }
      } else {
        const { error } = await supabase
          .from('timeline_items')
          .insert({
            ...base,
            order_index: timelineItems.length,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) {
          console.error('Error inserting timeline item:', error);
          showNotification(`Erreur lors de l'ajout: ${error.message}`, 'error');
          throw error;
        }
      }

      await loadCampaignData();
      handleCloseTimelineModal();
    } catch (error) {
      console.error('Error adding timeline item:', error);
    }
  };

  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsUploading(true);

      // Mode édition
      if (editingItemId) {
        // Créer l'objet de mise à jour de base
        let updateData: any = {
          name: documentForm.name,
          type: documentForm.type,
          visible_for_brand: documentForm.visibleForBrand,
          updated_at: new Date().toISOString()
        };

        // Si un lien est fourni
        if (uploadMode === 'link' && documentForm.url) {
          updateData.file_url = documentForm.url;
          updateData.file_name = documentForm.name;
          updateData.file_size = null;
        }
        // Si un nouveau fichier est sélectionné, l'uploader
        else if (selectedFile) {
          if (selectedFile.size > 500 * 1024 * 1024) {
            showNotification('Le fichier est trop volumineux. La taille maximale est de 500 MB.', 'error');
            setIsUploading(false);
            return;
          }

          const sanitizedFileName = sanitizeFileName(selectedFile.name);
          const fileName = `${Date.now()}_${sanitizedFileName}`;
          const filePath = `documents/${id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('campaign-documents')
            .upload(filePath, selectedFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: urlData } = supabase.storage
            .from('campaign-documents')
            .getPublicUrl(filePath);

          updateData.file_url = urlData.publicUrl;
          updateData.file_name = selectedFile.name;
          updateData.file_size = selectedFile.size;
        }

        // Gérer les champs d'influenceur
        if (selectedInfluencerId && selectedInfluencerId.trim() !== '') {
          updateData.influencer_id = selectedInfluencerId;
          updateData.influencer_name = selectedInfluencerName;
        } else {
          updateData.influencer_id = null;
          updateData.influencer_name = null;
        }

        const { error: updateError } = await supabase
          .from('collab_documents')
          .update(updateData)
          .eq('id', editingItemId);

        if (updateError) throw updateError;
      } else {
        // Mode création - fichier ou lien obligatoire
        if (uploadMode === 'file' && !selectedFile) {
          showNotification('Veuillez sélectionner un fichier', 'error');
          return;
        }
        if (uploadMode === 'file' && selectedFile && selectedFile.size > 500 * 1024 * 1024) {
          showNotification('Le fichier est trop volumineux. La taille maximale est de 500 MB.', 'error');
          return;
        }
        if (uploadMode === 'link' && !documentForm.url) {
          showNotification('Veuillez entrer un lien', 'error');
          return;
        }

        let downloadURL = '';
        let fileName = documentForm.name;
        let fileSize = null;

        if (uploadMode === 'link') {
          downloadURL = documentForm.url;
        } else {
          const sanitizedFileName = sanitizeFileName(selectedFile!.name);
          const fileNameWithTimestamp = `${Date.now()}_${sanitizedFileName}`;
          const filePath = `documents/${id}/${fileNameWithTimestamp}`;

          const { error: uploadError } = await supabase.storage
            .from('campaign-documents')
            .upload(filePath, selectedFile!, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: urlData } = supabase.storage
            .from('campaign-documents')
            .getPublicUrl(filePath);

          downloadURL = urlData.publicUrl;
          fileName = selectedFile!.name;
          fileSize = selectedFile!.size;
        }

        let documentData: any = {
          collab_id: id,
          name: documentForm.name,
          type: documentForm.type,
          file_url: downloadURL,
          file_name: fileName,
          file_size: fileSize,
          visible_for_brand: documentForm.visibleForBrand,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        if (selectedInfluencerId && selectedInfluencerId.trim() !== '') {
          documentData.influencer_id = selectedInfluencerId;
          documentData.influencer_name = selectedInfluencerName;
        }

        const { error: insertError } = await supabase
          .from('collab_documents')
          .insert(documentData);

        if (insertError) throw insertError;
      }

      setUploadProgress(100);
      await loadCampaignData();
      setIsDocumentModalOpen(false);
      resetDocumentForm();
      setEditingItemId(null);
      setSelectedInfluencerId('');
      setSelectedInfluencerName('');
      setIsUploading(false);
      setUploadProgress(0);
    } catch (error: any) {
      console.error('Error saving document:', error);
      showNotification(`Erreur: ${error?.message || error}`, 'error');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAddStats = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Créer l'objet de base sans les champs d'influenceur
      let statsData: any = {
        collabId: id,
        creator: statsForm.creator,
        views: parseInt(String(statsForm.views)),
        likes: parseInt(String(statsForm.likes)),
        engagementRate: parseFloat(String(statsForm.engagementRate)),
        postUrl: statsForm.postUrl,
        postThumb: statsForm.postThumb,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Ajouter les champs d'influenceur seulement si un influenceur est sélectionné
      if (selectedInfluencerId && selectedInfluencerId.trim() !== '') {
        statsData.influencerId = selectedInfluencerId;
        statsData.influencerName = selectedInfluencerName;
      }

      await createDocument('collab_stats', statsData);
      
      await loadCampaignData();
      setIsStatsModalOpen(false);
      resetStatsForm();
      setSelectedInfluencerId('');
      setSelectedInfluencerName('');
    } catch (error) {
      console.error('Error adding stats:', error);
    }
  };

  const handleDeleteItem = async (type: string, itemId: string) => {
    try {
      let tableName = '';

      if (type === 'timeline') {
        tableName = 'timeline_items';
      } else if (type === 'document') {
        tableName = 'collab_documents';
      } else if (type === 'stats') {
        tableName = 'collab_stats';
      }

      if (tableName) {
        const { error } = await supabase
          .from(tableName)
          .delete()
          .eq('id', itemId);

        if (error) throw error;
      }

      await loadCampaignData();
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleGeneratePublicLink = async () => {
    try {
      if (!campaign) return;

      const linkToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

      const { error } = await supabase
        .from('collabs')
        .update({
          link_token: linkToken,
          link_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaign.id!);

      if (error) {
        throw error;
      }

      await loadCampaignData();
      setIsLinkModalOpen(true);
    } catch (error) {
      console.error('Error generating public link:', error);
    }
  };

  const copyPublicLink = async () => {
    if (!campaign?.linkToken) return;
    
    const publicUrl = `${window.location.origin}/suivi/${campaign.linkToken}`;
    await navigator.clipboard.writeText(publicUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const toggleLinkStatus = async () => {
    try {
      if (!campaign) return;

      const { error } = await supabase
        .from('collabs')
        .update({
          link_active: !campaign.linkActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaign.id!);

      if (error) {
        throw error;
      }

      await loadCampaignData();
    } catch (error) {
      console.error('Error toggling link status:', error);
    }
  };

  const resetTimelineForm = () => {
    setTimelineForm({
      stepName: '',
      deadline: '',
      status: 'todo',
      order: 0,
      influencerId: '',
      influencerName: ''
    });
  };

  const resetDocumentForm = () => {
    setDocumentForm({
      name: '',
      type: 'brief',
      visibleForBrand: false,
      influencerId: '',
      influencerName: '',
      url: ''
    });
    setSelectedInfluencerId('');
    setSelectedInfluencerName('');
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadMode('file');
  };

  const resetStatsForm = () => {
    setStatsForm({
      creator: '',
      views: 0,
      likes: 0,
      engagementRate: 0,
      postUrl: '',
      postThumb: '',
      influencerId: '',
      influencerName: ''
    });
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return 'Date non définie';
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const addToCalendar = async (item: TimelineItem, influencerName: string) => {
    if (!item.deadline) {
      showNotification('Cette tâche n\'a pas d\'échéance définie', 'error');
      return;
    }

    if (!item.influencerId) {
      showNotification('Cette tâche n\'est pas assignée à un influenceur', 'error');
      return;
    }

    try {
      // Convert deadline to Date object
      let deadlineDate: Date;
      if (typeof item.deadline === 'string') {
        deadlineDate = new Date(item.deadline);
      } else if (typeof item.deadline === 'object' && 'seconds' in item.deadline) {
        deadlineDate = new Date(item.deadline.seconds * 1000);
      } else {
        deadlineDate = new Date(item.deadline);
      }

      // Format date as YYYY-MM-DD for database
      const year = deadlineDate.getFullYear();
      const month = String(deadlineDate.getMonth() + 1).padStart(2, '0');
      const day = String(deadlineDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      // First, try to get influencer from the loaded campaign data
      let influencerSlug = '';
      let instagramUsername = '';

      const influencerFromCampaign = campaign?.influencers?.find(inf => inf.id === item.influencerId);

      if (influencerFromCampaign) {
        // Use data from campaign
        instagramUsername = influencerFromCampaign.instagramUsername || '';
        if (instagramUsername) {
          influencerSlug = instagramUsername.toLowerCase().replace(/[^a-z0-9]/g, '-');
        } else {
          influencerSlug = influencerName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        }
      } else {
        // Fallback: try to get from influencers table
        const { data: influencerData, error: influencerError } = await supabase
          .from('influencers')
          .select('id, username, display_name')
          .eq('id', item.influencerId)
          .maybeSingle();

        if (influencerError || !influencerData) {
          console.error('Error fetching influencer:', influencerError);
          showNotification('Impossible de récupérer les informations de l\'influenceur', 'error');
          return;
        }

        instagramUsername = influencerData.username || '';
        if (instagramUsername) {
          influencerSlug = instagramUsername.toLowerCase().replace(/[^a-z0-9]/g, '-');
        } else {
          influencerSlug = (influencerData.display_name || influencerName).toLowerCase().replace(/[^a-z0-9]/g, '-');
        }
      }

      // Check if brief exists for this date and influencer
      const { data: existingBriefs, error: briefError } = await supabase
        .from('daily_briefs')
        .select('*')
        .eq('influencer_id', item.influencerId)
        .eq('date', dateString);

      if (briefError) {
        console.error('Error checking briefs:', briefError);
        showNotification('Erreur lors de la vérification du brief', 'error');
        return;
      }

      const newTask = {
        id: `task-${Date.now()}`,
        text: `${campaign?.name || 'Campagne'} - ${item.stepName}`,
        completed: false,
        added_by: 'agency',
        campaign_name: campaign?.name,
        campaign_brand: campaign?.brand
      };

      if (existingBriefs && existingBriefs.length > 0) {
        // Add task to existing brief
        const brief = existingBriefs[0];
        const updatedTasks = [...(brief.tasks || []), newTask];

        const { error: updateError } = await supabase
          .from('daily_briefs')
          .update({
            tasks: updatedTasks,
            updated_at: new Date().toISOString()
          })
          .eq('id', brief.id);

        if (updateError) {
          console.error('Error updating brief:', updateError);
          showNotification('Erreur lors de l\'ajout de la tâche', 'error');
          return;
        }
      } else {
        // Create new brief with task
        const { error: insertError } = await supabase
          .from('daily_briefs')
          .insert({
            agency_id: campaign?.agency_id || '00000000-0000-0000-0000-000000000000',
            influencer_id: item.influencerId,
            influencer_name: influencerName,
            influencer_slug: influencerSlug,
            agency_name: campaign?.agency_name || campaign?.brand || 'Campaly',
            date: dateString,
            tasks: [newTask],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error creating brief:', insertError);
          showNotification('Erreur lors de la création du brief', 'error');
          return;
        }
      }

      showNotification(`Tâche ajoutée au calendrier de ${influencerName} pour le ${deadlineDate.toLocaleDateString('fr-FR')}`, 'success');
    } catch (error) {
      console.error('Error adding to calendar:', error);
      showNotification('Erreur lors de l\'ajout au calendrier', 'error');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'late':
        return <Info className="w-5 h-5 text-red-500" />;
      default:
        return <CheckSquare className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'done':
        return 'Terminé';
      case 'in_progress':
        return 'En cours';
      case 'late':
        return 'En retard';
      default:
        return 'À faire';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-500/20 text-green-300 border-green-500/20';
      case 'in_progress':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20';
      case 'late':
        return 'bg-red-500/20 text-red-300 border-red-500/20';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/20';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/10 border-t-white/60"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <Info className="w-8 h-8 text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Erreur</h3>
        <p className="text-white/50">{error || 'Impossible de charger les détails de la collaboration'}</p>
        <button
          onClick={() => navigate('/app/collaborations')}
          className="mt-4 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
        >
          Retour aux collaborations
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Handshake className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {campaign.name}
            </h1>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-white/40" />
              <p className="text-white/50">
                {campaign.brand}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/app/collaborations/new?id=${id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </button>
          <button
            onClick={() => campaign.linkToken ? setIsLinkModalOpen(true) : handleGeneratePublicLink()}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
          >
            <LinkIcon className="w-4 h-4" />
            {campaign.linkToken ? 'Gérer le lien' : 'Générer un lien'}
          </button>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/[0.04] border-white/[0.08] rounded-xl p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-white/50">Date</h2>
            <Calendar className="w-5 h-5 text-accent-blue" />
          </div>
          <p className="text-xl font-semibold text-white">
            {formatDate(campaign.signed_date)}
          </p>
        </div>

        <div className="bg-white/[0.04] border-white/[0.08] rounded-xl p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-white/50">Chef de projet</h2>
            <Users className="w-5 h-5 text-accent-blue" />
          </div>
          <p className="text-xl font-semibold text-white">
            {campaign.manager}
          </p>
        </div>

        <div className="bg-white/[0.04] border-white/[0.08] rounded-xl p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-white/50">Budget</h2>
            <BarChart3 className="w-5 h-5 text-accent-blue" />
          </div>
          <p className="text-xl font-semibold text-white">
            {formatCurrency(campaign.budget || campaign.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0))}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/[0.04] border-white/[0.08] rounded-xl border">
        <div className="border-b border-white/[0.06]">
          <nav className="flex -mb-px">
            {[
              { id: 'timeline', label: 'Timeline', icon: Calendar },
              { id: 'influencers', label: 'Influenceurs', icon: Users },
              { id: 'documents', label: 'Documents et preview', icon: FileText }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 font-medium text-sm border-b-2 ${
                    activeTab === tab.id
                      ? 'border-white text-white'
                      : 'border-transparent text-white/40 hover:text-white/60 hover:border-white/20'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-white">
                  Avancement de la campagne
                </h2>
                <button
                  onClick={() => {
                    resetTimelineForm();
                    setIsTimelineModalOpen(true);
                    setIsEditingTimeline(false);
                    setEditingItemId(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
                >
                  <Plus className="w-4 h-4" />
                  {isEditingTimeline ? 'Modifier l\'étape' : 'Ajouter une étape'}
                </button>
              </div>

              {timelineItems.length === 0 ? (
                <div className="text-center py-12 bg-white/[0.02] rounded-xl">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-6 h-6 text-white/30" />
                  </div>
                  <p className="text-white/30">
                    Aucune étape définie pour cette campagne
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {(() => {
                    const groupedItems = timelineItems.reduce((acc, item) => {
                      const key = item.influencerId || 'common';
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(item);
                      return acc;
                    }, {} as Record<string, typeof timelineItems>);

                    const commonItems = groupedItems['common'] || [];
                    const influencerGroups = Object.entries(groupedItems).filter(([key]) => key !== 'common');

                    return (
                      <>
                        {commonItems.length > 0 && (
                          <div className="space-y-4">
                            <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">
                              Étapes communes
                            </h3>
                            <div className="space-y-4">
                              {commonItems.map((item, index) => (
                                <div
                                  key={item.id}
                                  className={`relative cursor-grab active:cursor-grabbing ${
                                    draggingId === item.id ? 'ring-2 ring-blue-500' : ''
                                  }`}
                                  draggable
                                  onDragStart={() => onDragStart(item.id)}
                                  onDragOver={(e) => { e.preventDefault(); onDragOver(item.id); }}
                                  onDrop={(e) => { e.preventDefault(); onDrop(); }}
                                >
                                  {index < commonItems.length - 1 && (
                                    <div className="absolute top-8 left-4 bottom-0 w-0.5 bg-white/[0.06]"></div>
                                  )}

                                  <div className="flex items-start gap-4">
                                    <div
                                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        item.status === 'done' ? 'bg-green-500/20 text-green-300 cursor-pointer' :
                                        item.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-300 cursor-pointer' :
                                        item.status === 'late' ? 'bg-red-500/20 text-red-300 cursor-pointer' :
                                        'bg-gray-500/20 text-gray-300 cursor-pointer'
                                      }`}
                                      onClick={() => {
                                        const nextStatus =
                                          item.status === 'todo' ? 'in_progress' :
                                          item.status === 'in_progress' ? 'done' :
                                          'todo';
                                        handleTimelineStatusChange(item.id, nextStatus);
                                      }}
                                      title="Cliquer pour changer le statut"
                                    >
                                      {item.status === 'done' ? (
                                        <CheckCircle className="w-5 h-5" />
                                      ) : item.status === 'in_progress' ? (
                                        <Clock className="w-5 h-5" />
                                      ) : item.status === 'late' ? (
                                        <Info className="w-5 h-5" />
                                      ) : (
                                        <CheckSquare className="w-5 h-5" />
                                      )}
                                    </div>

                                    <div className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
                                      <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium text-white">{item.stepName}</h3>
                                        <div className="flex items-center gap-2">
                                          <GripVertical className="w-4 h-4 text-white/20 cursor-grab active:cursor-grabbing select-none" />
                                          <button
                                            onClick={() => {
                                              const nextStatus =
                                                item.status === 'todo' ? 'in_progress' :
                                                item.status === 'in_progress' ? 'done' :
                                                'todo';
                                              handleTimelineStatusChange(item.id, nextStatus);
                                            }}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                            item.status === 'done' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20' :
                                            item.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20' :
                                            item.status === 'late' ? 'bg-red-500/20 text-red-400 border-red-500/20' :
                                            'bg-blue-500/20 text-blue-400 border-blue-500/20'
                                          } cursor-pointer hover:opacity-80 transition-opacity`}>
                                            {item.status === 'done' ? <CheckCircle className="w-3.5 h-3.5" /> :
                                             item.status === 'in_progress' ? <Clock className="w-3.5 h-3.5" /> :
                                             item.status === 'late' ? <AlertCircle className="w-3.5 h-3.5" /> :
                                             <CheckSquare className="w-3.5 h-3.5" />}
                                            {item.status === 'done' ? 'Terminé' :
                                             item.status === 'in_progress' ? 'En cours' :
                                             item.status === 'late' ? 'En retard' :
                                             'À faire'}
                                          </button>
                                          {item.deadline && (
                                            <button
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={() => {
                                                const influencerId = item.influencerId || campaign?.influencers?.[0]?.id || campaign?.influencer_ids?.[0];
                                                const influencerName = item.influencerName || campaign?.influencers?.[0]?.fullName || campaign?.influencers?.[0]?.full_name || campaign?.brand || 'Influenceur';
                                                addToCalendar({ ...item, influencerId } as TimelineItem, influencerName);
                                              }}
                                              className="p-1 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                                              title="Ajouter au calendrier"
                                            >
                                              <Calendar className="w-4 h-4" />
                                            </button>
                                          )}
                                          <button
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={() => openEditTimelineItem(item as TimelineItem)}
                                            className="p-1 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                                            title="Modifier cette étape"
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </button>
                                          <button
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={() => handleDeleteTimelineItem(item.id!)}
                                            className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Supprimer cette étape"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>

                                      {item.deadline && (
                                        <div className="flex items-center gap-2 text-sm text-white/50">
                                          <Calendar className="w-4 h-4 text-white/30" />
                                          <span>Échéance: {formatDate(item.deadline)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {influencerGroups.map(([influencerId, items]) => {
                          const influencer = campaign.influencers?.find(inf => inf.id === influencerId);
                          const influencerName = influencer?.fullName || items[0]?.influencerName || 'Influenceur';

                          return (
                            <div key={influencerId} className="space-y-4">
                              <div className="flex items-center gap-3">
                                {influencer && (
                                  <InfluencerAvatar
                                    profileImageUrl={influencer.profile_image_url || null}
                                    name={influencerName}
                                    size="sm"
                                  />
                                )}
                                <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">
                                  {influencerName}
                                </h3>
                              </div>
                              <div className="space-y-4">
                                {items.map((item, index) => (
                                  <div
                                    key={item.id}
                                    className={`relative cursor-grab active:cursor-grabbing ${
                                      draggingId === item.id ? 'ring-2 ring-blue-500' : ''
                                    }`}
                                    draggable
                                    onDragStart={() => onDragStart(item.id)}
                                    onDragOver={(e) => { e.preventDefault(); onDragOver(item.id); }}
                                    onDrop={(e) => { e.preventDefault(); onDrop(); }}
                                  >
                                    {index < items.length - 1 && (
                                      <div className="absolute top-8 left-4 bottom-0 w-0.5 bg-white/[0.06]"></div>
                                    )}

                                    <div className="flex items-start gap-4">
                                      <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                          item.status === 'done' ? 'bg-green-500/20 text-green-300 cursor-pointer' :
                                          item.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-300 cursor-pointer' :
                                          item.status === 'late' ? 'bg-red-500/20 text-red-300 cursor-pointer' :
                                          'bg-gray-500/20 text-gray-300 cursor-pointer'
                                        }`}
                                        onClick={() => {
                                          const nextStatus =
                                            item.status === 'todo' ? 'in_progress' :
                                            item.status === 'in_progress' ? 'done' :
                                            'todo';
                                          handleTimelineStatusChange(item.id, nextStatus);
                                        }}
                                        title="Cliquer pour changer le statut"
                                      >
                                        {item.status === 'done' ? (
                                          <CheckCircle className="w-5 h-5" />
                                        ) : item.status === 'in_progress' ? (
                                          <Clock className="w-5 h-5" />
                                        ) : item.status === 'late' ? (
                                          <Info className="w-5 h-5" />
                                        ) : (
                                          <CheckSquare className="w-5 h-5" />
                                        )}
                                      </div>

                                      <div className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <h3 className="font-medium text-white">{item.stepName}</h3>
                                          <div className="flex items-center gap-2">
                                            <GripVertical className="w-4 h-4 text-white/20 cursor-grab active:cursor-grabbing select-none" />
                                            <button
                                              onClick={() => {
                                                const nextStatus =
                                                  item.status === 'todo' ? 'in_progress' :
                                                  item.status === 'in_progress' ? 'done' :
                                                  'todo';
                                                handleTimelineStatusChange(item.id, nextStatus);
                                              }}
                                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                              item.status === 'done' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                                              item.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                              item.status === 'late' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                              'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                            } cursor-pointer hover:opacity-80 transition-opacity`}>
                                              {item.status === 'done' ? <CheckCircle className="w-3.5 h-3.5" /> :
                                               item.status === 'in_progress' ? <Clock className="w-3.5 h-3.5" /> :
                                               item.status === 'late' ? <AlertCircle className="w-3.5 h-3.5" /> :
                                               <CheckSquare className="w-3.5 h-3.5" />}
                                              {item.status === 'done' ? 'Terminé' :
                                               item.status === 'in_progress' ? 'En cours' :
                                               item.status === 'late' ? 'En retard' :
                                               'À faire'}
                                            </button>
                                            {item.deadline && (
                                              <button
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={() => {
                                                  const effectiveInfluencerId = item.influencerId || campaign?.influencers?.[0]?.id || campaign?.influencer_ids?.[0];
                                                  const effectiveInfluencerName = item.influencerName || campaign?.influencers?.[0]?.fullName || campaign?.influencers?.[0]?.full_name || influencerName || campaign?.brand || 'Influenceur';
                                                  addToCalendar({ ...item, influencerId: effectiveInfluencerId } as TimelineItem, effectiveInfluencerName);
                                                }}
                                                className="p-1 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                                                title="Ajouter au calendrier"
                                              >
                                                <Calendar className="w-4 h-4" />
                                              </button>
                                            )}
                                            <button
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={() => openEditTimelineItem(item as TimelineItem)}
                                              className="p-1 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                                              title="Modifier cette étape"
                                            >
                                              <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={() => handleDeleteTimelineItem(item.id!)}
                                              className="p-1 text-red-400 hover:text-red-300 hover:bg-white/[0.08] rounded-lg transition-colors"
                                              title="Supprimer cette étape"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>

                                        {item.deadline && (
                                          <div className="flex items-center gap-2 text-sm text-white/50">
                                            <Calendar className="w-4 h-4 text-white/30" />
                                            <span>Échéance: {formatDate(item.deadline)}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Influencers Tab */}
          {activeTab === 'influencers' && (
            <div>
              <h2 className="text-lg font-medium text-white mb-6">
                Influenceurs sélectionnés
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {campaign.influencers && campaign.influencers.length > 0 ? (
                  campaign.influencers.map((influencer, index) => (
                    <div
                      key={index}
                      className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-4"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <InfluencerAvatar
                            profileImageUrl={influencer.profile_image_url || null}
                            name={influencer.fullName}
                            size="md"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-white">
                              {influencer.fullName}
                            </h3>
                            <p className="text-sm text-white/50">
                              #{index + 1}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-semibold text-white">
                              {formatCurrency(influencer.rate || '0')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {influencer.deliverables && influencer.deliverables.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-white/60 mb-2">
                            Livrables:
                          </h4>
                          <div className="space-y-2 divide-y divide-white/[0.04]">
                            {influencer.deliverables.map((deliverable, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm py-2">
                                <div className="text-white/50">
                                  <span className="text-white/70">
                                    {deliverable.type}
                                  </span>
                                  {deliverable.description && (
                                    <span> - {deliverable.description}</span>
                                  )}
                                </div>
                                <span className="font-medium text-white">
                                  {formatCurrency(deliverable.price || '0')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-4 pt-4 border-t border-white/[0.06]">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/50">
                            Facture créateur
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              influencer.creator_invoice_sent
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/20'
                            }`}>
                              {influencer.creator_invoice_sent ? 'Reçue' : 'En attente'}
                            </span>
                            {influencer.paidPercentage !== undefined && (
                              <span className="text-sm text-white/60">
                                ({influencer.paidPercentage}%)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-12 bg-white/[0.02] rounded-xl">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
                      <Users className="w-6 h-6 text-white/30" />
                    </div>
                    <p className="text-white/30">
                      Aucun influenceur associé à cette campagne
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-white">
                  Documents et preview
                </h2>
                <button
                  onClick={() => {
                    resetDocumentForm();
                    setEditingItemId(null);
                    setUploadMode('file');
                    setIsDocumentModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un document
                </button>
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12 bg-white/[0.02] rounded-xl">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
                    <FileImage className="w-6 h-6 text-white/30" />
                  </div>
                  <p className="text-white/30">
                    Aucun document ou preview disponible pour cette campagne
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {documents.map((document) => (
                    <div
                      key={document.id}
                      className="bg-white/[0.04] rounded-xl border border-white/[0.08] p-4"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center">
                          <FileText className="w-5 h-5 text-white/50" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white truncate">
                            {document.name}
                          </h3>
                          <p className="text-sm text-white/50">
                            {document.type}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setDocumentForm({
                                name: document.name,
                                type: document.type,
                                visibleForBrand: document.visibleForBrand || false,
                                influencerId: document.influencerId || '',
                                influencerName: document.influencerName || '',
                                url: !document.fileSize ? document.fileUrl : ''
                              });
                              setUploadMode(!document.fileSize ? 'link' : 'file');
                              setEditingItemId(document.id);
                              setIsDocumentModalOpen(true);
                            }}
                            className="p-1 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg"
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedItem({ ...document, type: 'document' });
                              setIsDeleteModalOpen(true);
                            }}
                            className="p-1 text-red-400 hover:text-red-300 hover:bg-white/[0.08] rounded-lg"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {!document.fileSize && (
                          <div className="text-xs text-white/40 truncate">
                            <LinkIcon className="w-3 h-3 inline mr-1" />
                            {document.fileUrl}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            {document.fileSize && (
                              <span className="text-white/40">
                                {formatFileSize(document.fileSize)}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              document.visibleForBrand
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                                : 'bg-white/[0.06] text-white/40 border border-white/[0.08]'
                            }`}>
                              {document.visibleForBrand ? 'Visible pour la marque' : 'Interne'}
                            </span>
                          </div>
                          <a
                            href={encodeSupabaseUrl(document.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-white/50 hover:text-white transition-colors"
                          >
                            {document.fileSize ? (
                              <>
                                <Download className="w-4 h-4" />
                                <span>Télécharger</span>
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-4 h-4" />
                                <span>Ouvrir</span>
                              </>
                            )}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Timeline Modal */}
      <Dialog
        open={isTimelineModalOpen}
        onClose={handleCloseTimelineModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseTimelineModal}
                className="text-white/40 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddTimelineItem} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  {isEditingTimeline ? 'Modifier l\'étape' : 'Ajouter une étape'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Nom de l'étape
                    </label>
                    <input
                      type="text"
                      value={timelineForm.stepName}
                      onChange={(e) => setTimelineForm({ ...timelineForm, stepName: e.target.value })}
                      className="mt-1 block w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                      placeholder="Ex: Validation du brief"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/60 mb-1">
                        Échéance (optionnel)
                      </label>
                      <input
                        type="date"
                        value={timelineForm.deadline}
                        onChange={(e) => setTimelineForm({ ...timelineForm, deadline: e.target.value })}
                        className="mt-1 block w-full bg-white/[0.06] border border-white/[0.08] text-white rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/60 mb-1">
                        Statut
                      </label>
                      <CustomListbox
                        value={timelineForm.status}
                        onChange={(value) => setTimelineForm({ ...timelineForm, status: value as TimelineItem['status'] })}
                        options={[
                          { value: 'todo', label: 'À faire' },
                          { value: 'in_progress', label: 'En cours' },
                          { value: 'done', label: 'Terminé' },
                          { value: 'late', label: 'En retard' }
                        ]}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">
                      Influenceur (optionnel)
                    </label>
                    <CustomListbox
                      value={selectedInfluencerId}
                      onChange={(value) => {
                        setSelectedInfluencerId(value);
                        const influencer = campaign?.influencers.find(inf => inf.id === value);
                        setSelectedInfluencerName(influencer?.fullName || '');
                      }}
                      options={[
                        { value: '', label: 'Aucun influenceur spécifique' },
                        ...(campaign?.influencers.map((influencer) => ({
                          value: influencer.id || '',
                          label: influencer.fullName
                        })) || [])
                      ]}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseTimelineModal}
                    className="px-4 py-2 text-sm font-medium rounded-xl bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium bg-white text-black font-semibold rounded-xl hover:bg-white/90"
                  >
                    {isEditingTimeline ? 'Enregistrer' : 'Ajouter'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Document Modal */}
      <Dialog
        open={isDocumentModalOpen}
        onClose={() => {
          setIsDocumentModalOpen(false);
          setEditingItemId(null);
          setSelectedFile(null);
        }}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => {
                  setIsDocumentModalOpen(false);
                  setEditingItemId(null);
                  setSelectedFile(null);
                }}
                className="text-white/40 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className="text-xl font-bold text-white mb-6">
              {editingItemId ? 'Modifier le document' : 'Ajouter un document'}
            </h2>

            <form onSubmit={handleAddDocument} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Nom du document *
                </label>
                <input
                  type="text"
                  value={documentForm.name}
                  onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Influenceur concerné (optionnel)
                </label>
                <CustomListbox
                  value={selectedInfluencerId}
                  onChange={(value) => {
                    setSelectedInfluencerId(value);
                    if (value) {
                      const inf = campaign?.influencers.find(i => i.id === value);
                      const infName = inf?.fullName || '';
                      setSelectedInfluencerName(infName);
                      setDocumentForm(prev => ({
                        ...prev,
                        influencerId: value,
                        influencerName: infName
                      }));
                    } else {
                      setSelectedInfluencerName('');
                      setDocumentForm(prev => ({
                        ...prev,
                        influencerId: '',
                        influencerName: ''
                      }));
                    }
                  }}
                  options={[
                    { value: '', label: 'Tous les influenceurs' },
                    ...(campaign?.influencers.map((influencer) => ({
                      value: influencer.id,
                      label: influencer.fullName
                    })) || [])
                  ]}
                />
                <p className="mt-1 text-sm text-white/30">
                  Si ce document concerne un influenceur spécifique, sélectionnez-le ici.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Type
                </label>
                <CustomListbox
                  value={documentForm.type}
                  onChange={(value) => setDocumentForm({ ...documentForm, type: value })}
                  options={[
                    { value: 'brief', label: 'Brief' },
                    { value: 'contract', label: 'Contrat' },
                    { value: 'preview', label: 'Preview' },
                    { value: 'other', label: 'Autre' }
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  {editingItemId ? 'Fichier ou lien (optionnel - laissez vide pour conserver)' : 'Fichier ou lien *'}
                </label>

                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setUploadMode('file')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl border transition-colors ${
                      uploadMode === 'file'
                        ? 'bg-white text-black border-white font-semibold'
                        : 'bg-white/[0.06] text-white/60 border-white/[0.08] hover:bg-white/[0.10]'
                    }`}
                  >
                    Fichier
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode('link')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl border transition-colors ${
                      uploadMode === 'link'
                        ? 'bg-white text-black border-white font-semibold'
                        : 'bg-white/[0.06] text-white/60 border-white/[0.08] hover:bg-white/[0.10]'
                    }`}
                  >
                    Lien
                  </button>
                </div>

                {uploadMode === 'file' ? (
                  <div
                    className="border-2 border-dashed border-white/[0.08] rounded-xl p-6 text-center cursor-pointer hover:border-white/20 bg-white/[0.02]"
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-white/40" />
                      {selectedFile ? (
                        <div className="text-white">
                          <p className="font-medium">{selectedFile.name}</p>
                          <p className="text-sm text-white/50">
                            {formatFileSize(selectedFile.size)}
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-white">
                            {editingItemId ? 'Cliquez pour remplacer le fichier' : 'Cliquez pour sélectionner un fichier'}
                          </p>
                          <p className="text-sm text-white/40">
                            PDF, Word, Excel, Images, Vidéos, etc. (Max 500MB)
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <input
                    type="url"
                    value={documentForm.url}
                    onChange={(e) => setDocumentForm({ ...documentForm, url: e.target.value })}
                    placeholder="https://exemple.com/document.pdf"
                    className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="visibleForBrand"
                  checked={documentForm.visibleForBrand}
                  onChange={(e) => setDocumentForm({ ...documentForm, visibleForBrand: e.target.checked })}
                  className="h-4 w-4 rounded bg-white/[0.06] border-white/[0.08]"
                />
                <label
                  htmlFor="visibleForBrand"
                  className="text-sm text-white/60"
                >
                  Visible pour la marque
                </label>
              </div>

              {isUploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/50">
                      Téléchargement en cours...
                    </span>
                    <span className="text-sm text-white/50">
                      {uploadProgress.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-white/[0.06] rounded-full h-2">
                    <div
                      className="bg-white/60 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsDocumentModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={(uploadMode === 'file' && !selectedFile && !editingItemId) || (uploadMode === 'link' && !documentForm.url && !editingItemId) || isUploading}
                  className="px-4 py-2 text-sm font-medium bg-white text-black font-semibold rounded-xl hover:bg-white/90 disabled:opacity-50"
                >
                  {isUploading ? 'Téléchargement...' : editingItemId ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Stats Modal */}
      <Dialog
        open={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsStatsModalOpen(false)}
                className="text-white/40 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className="text-xl font-bold text-white mb-6">
              Ajouter des statistiques
            </h2>

            <form onSubmit={handleAddStats} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Créateur *
                </label>
                <input
                  type="text"
                  value={statsForm.creator}
                  onChange={(e) => setStatsForm({ ...statsForm, creator: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Influenceur concerné (optionnel)
                </label>
                <CustomListbox
                  value={selectedInfluencerId}
                  onChange={(value) => {
                    setSelectedInfluencerId(value);
                    if (value) {
                      const inf = campaign?.influencers.find(i => i.id === value);
                      setSelectedInfluencerName(inf?.fullName || '');
                      setStatsForm(prev => ({...prev, creator: inf?.fullName || ''}));
                    } else {
                      setSelectedInfluencerName('');
                    }
                  }}
                  options={[
                    { value: '', label: 'Sélectionner un influenceur' },
                    ...(campaign?.influencers.map((influencer) => ({
                      value: influencer.id,
                      label: influencer.fullName
                    })) || [])
                  ]}
                />
                <p className="mt-1 text-sm text-white/30">
                  Sélectionnez l'influenceur dont vous ajoutez les statistiques.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Vues
                  </label>
                  <input
                    type="number"
                    value={statsForm.views}
                    onChange={(e) => setStatsForm({ ...statsForm, views: parseInt(e.target.value) })}
                    className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Likes
                  </label>
                  <input
                    type="number"
                    value={statsForm.likes}
                    onChange={(e) => setStatsForm({ ...statsForm, likes: parseInt(e.target.value) })}
                    className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Taux d'engagement (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={statsForm.engagementRate}
                  onChange={(e) => setStatsForm({ ...statsForm, engagementRate: parseFloat(e.target.value) })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  URL du post
                </label>
                <input
                  type="url"
                  value={statsForm.postUrl}
                  onChange={(e) => setStatsForm({ ...statsForm, postUrl: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  placeholder="https://"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  URL de l'image (miniature)
                </label>
                <input
                  type="url"
                  value={statsForm.postThumb}
                  onChange={(e) => setStatsForm({ ...statsForm, postThumb: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none px-3 py-2"
                  placeholder="https://"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsStatsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium bg-white text-black font-semibold rounded-xl hover:bg-white/90"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white">
                Confirmer la suppression
              </h3>
              <p className="mt-2 text-sm text-white/50">
                Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium bg-white/[0.08] text-white/60 hover:bg-white/[0.14] border border-white/[0.08] rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => selectedItem && handleDeleteItem(selectedItem.type, selectedItem.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Public Link Modal */}
      <Dialog
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsLinkModalOpen(false)}
                className="text-white/40 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className="text-xl font-bold text-white mb-6">
              Lien de partage
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  Lien public pour la marque
                </label>
                <div className="flex items-center gap-2 p-3 bg-white/[0.04] border border-white/[0.08] rounded-xl">
                  <input
                    type="text"
                    value={`${window.location.origin}/suivi/${campaign.linkToken}`}
                    readOnly
                    className="flex-1 bg-transparent border-none focus:ring-0 outline-none text-white/60 text-sm"
                  />
                  <button
                    onClick={copyPublicLink}
                    className="p-2 rounded-lg bg-white/[0.08] hover:bg-white/[0.14] text-white/60 transition-colors"
                  >
                    {copiedLink ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-white/60">
                  Statut du lien
                </span>
                <button
                  onClick={toggleLinkStatus}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    campaign.linkActive ? 'bg-white/80' : 'bg-white/[0.08]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full transition ${
                      campaign.linkActive ? 'translate-x-6 bg-black' : 'translate-x-1 bg-white/40'
                    }`}
                  />
                </button>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.04]">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 flex-shrink-0 text-white/40" />
                  <div className="text-sm text-white/50">
                    <p>Ce lien permet à la marque de suivre l'avancement de la campagne sans avoir besoin de se connecter.</p>
                    <p className="mt-1">Seuls les documents marqués comme "Visible pour la marque" seront accessibles.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}